import React from 'react';
import {
  ShoppingBagIcon,
  BuildingStorefrontIcon,
  CubeIcon,
  ShoppingCartIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import BreadCrumb from '../BreadCrumb';
import { useAuth } from '../Auth/Auth';
import { Link, Navigate } from 'react-router-dom';

const actions = [
  {
    title: 'New Job Card Prices',
    to: '/procurement/jobcardpricing',
    desc: "Add Cost Price to New Job Cards",
    icon: CurrencyDollarIcon,
    gradient: 'from-amber-500 to-orange-600',
  },
  {
    title: 'Edit Job Card Prices',
    to: '/procurement/editjobcardpricing',
    desc: "Edit Cost Price for Open Job Cards",
    icon: DocumentTextIcon,
    gradient: 'from-orange-500 to-red-600',
  },
  {
    title: 'Suppliers',
    to: '/procurement/suppliers',
    desc: "Register, Update or Delete Supplier Contact Details",
    icon: BuildingStorefrontIcon,
    gradient: 'from-blue-500 to-cyan-600',
  },
  {
    title: 'Inventory',
    to: '/procurement/inventory',
    desc: "Add Items Directly Purchased to Inventory",
    icon: CubeIcon,
    gradient: 'from-blue-500 to-cyan-600',
  },
  {
    title: 'Make Purchase Request',
    to: '/procurement/buyItem',
    desc: "Requisition from a Registered Vendor",
    icon: ShoppingCartIcon,
    gradient: 'from-purple-600 to-indigo-600',
  },
  {
    title: 'Update Delivery Status',
    to: '/procurement/manage',
    desc: "Update the Status of Items Ordered",
    icon: ClipboardDocumentListIcon,
    gradient: 'from-indigo-600 to-purple-600',
  },
  {
    title: 'Purchase Orders',
    to: '/procurement/purchase-orders',
    desc: "View and Manage Purchase Orders (Optional)",
    icon: DocumentTextIcon,
    gradient: 'from-blue-600 to-indigo-600',
  },
  {
    title: 'Goods Received Notes',
    to: '/procurement/grn',
    desc: "Inspect and Record Received Items with Quality Check",
    icon: ClipboardDocumentCheckIcon,
    gradient: 'from-green-500 to-emerald-600',
  },
];

const pages = [
  { name: 'Procurement', to: '/procurement', component: Link, current: true },
];

const Procurement = () => {
  const { role, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (role !== 'Procurement' && role !== 'Admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl shadow-lg">
              <ShoppingBagIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-gray-900">Procurement Management</h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage suppliers, inventory, requisitions, and purchase orders
              </p>
            </div>
          </div>
        </div>

        {/* Action Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {actions.map((action) => (
            <Link
              key={action.title}
              to={action.to}
              className="group relative bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
            >
              {/* Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${action.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>

              <div className="relative p-4">
                {/* Icon */}
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${action.gradient} shadow-lg mb-3`}>
                  <action.icon className="h-6 w-6 text-white" />
                </div>

                {/* Content */}
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors duration-300">
                  {action.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {action.desc}
                </p>

                {/* Arrow Icon */}
                <div className="absolute top-4 right-4 text-gray-300 group-hover:text-purple-600 transition-colors duration-300">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Procurement;
